import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { useAnalysisStore } from '../../store/analysisStore';
import { AppUserDTO, deleteUser, listUsers, Role, updateUser } from '../../utils/api';

const ROLE_OPTIONS: { value: Role; label: string; description: string }[] = [
  { value: 'user', label: 'User', description: 'Sees only their own assessments' },
  { value: 'contributor', label: 'Contributor', description: 'Sees and manages all assessments' },
  { value: 'admin', label: 'Admin', description: 'All contributor powers + user management' },
];

const ROLE_CHIP_COLOR: Record<Role, 'default' | 'primary' | 'secondary'> = {
  user: 'default',
  contributor: 'primary',
  admin: 'secondary',
};

export default function AdminUsersView() {
  const navigate = useNavigate();
  const { currentUser, firebaseToken } = useAnalysisStore();

  const [users, setUsers] = useState<AppUserDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingNotes, setPendingNotes] = useState<Record<string, string>>({});
  const [savingUid, setSavingUid] = useState<string | null>(null);

  const loadUsers = async () => {
    if (!firebaseToken) return;
    setLoading(true);
    setError(null);
    try {
      const fetched = await listUsers(firebaseToken);
      setUsers(fetched);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseToken]);

  if (!currentUser) {
    return (
      <Container sx={{ mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (currentUser.role !== 'admin') {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">Admin role required.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mt: 2 }}>
          Back
        </Button>
      </Container>
    );
  }

  const handleRoleChange = async (user: AppUserDTO, e: SelectChangeEvent<Role>) => {
    const next = e.target.value as Role;
    if (next === user.role) return;

    if (user.uid === currentUser.uid && next !== 'admin') {
      if (!window.confirm('You are about to demote yourself. If you are the last admin, the request will fail. Continue?')) {
        return;
      }
    }

    setSavingUid(user.uid);
    setError(null);
    try {
      const updated = await updateUser(firebaseToken, user.uid, { role: next });
      setUsers((prev) => prev.map((u) => (u.uid === user.uid ? updated : u)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSavingUid(null);
    }
  };

  const handleNotesSave = async (user: AppUserDTO) => {
    const next = pendingNotes[user.uid];
    if (next === undefined) return;
    setSavingUid(user.uid);
    setError(null);
    try {
      const updated = await updateUser(firebaseToken, user.uid, { notes: next });
      setUsers((prev) => prev.map((u) => (u.uid === user.uid ? updated : u)));
      setPendingNotes((prev) => {
        const { [user.uid]: _, ...rest } = prev;
        return rest;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSavingUid(null);
    }
  };

  const handleDelete = async (user: AppUserDTO) => {
    if (!window.confirm(`Delete ${user.email || user.uid}? Their assessments will remain attached to the deleted uid.`)) {
      return;
    }
    setSavingUid(user.uid);
    setError(null);
    try {
      await deleteUser(firebaseToken, user.uid);
      setUsers((prev) => prev.filter((u) => u.uid !== user.uid));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setSavingUid(null);
    }
  };

  const formatDate = (ms: number) => (ms ? new Date(ms).toLocaleString() : '—');

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={3}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
          >
            Trust Index
          </Link>
          <Typography color="text.primary">User Management</Typography>
        </Breadcrumbs>

        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
          gap={2}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              User Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Assign each user a role: <strong>User</strong> (own assessments),
              <strong> Contributor</strong> (sees all assessments), or <strong>Admin</strong> (also
              manages users). The first admin must be bootstrapped manually — see
              <code style={{ margin: '0 4px' }}>infrastructure/BOOTSTRAP.md</code>.
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
            <Button variant="outlined" onClick={loadUsers}>Refresh</Button>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/')}>
              Back
            </Button>
          </Stack>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflowX: 'auto' }}>
          <Table aria-label="users table" sx={{ minWidth: 720 }}>
            <TableHead sx={{ bgcolor: 'action.hover' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Last Login</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">No users yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  const isSelf = user.uid === currentUser.uid;
                  const notesValue = pendingNotes[user.uid] ?? user.notes ?? '';
                  const notesDirty = pendingNotes[user.uid] !== undefined && pendingNotes[user.uid] !== (user.notes ?? '');
                  return (
                    <TableRow key={user.uid} hover>
                      <TableCell>
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" fontWeight={500}>
                              {user.email || '(no email)'}
                            </Typography>
                            {isSelf && <Chip label="YOU" size="small" variant="outlined" />}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {user.uid}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Select
                            size="small"
                            value={user.role}
                            disabled={savingUid === user.uid}
                            onChange={(e) => handleRoleChange(user, e)}
                            sx={{ minWidth: 140 }}
                          >
                            {ROLE_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                <Tooltip title={opt.description} placement="right">
                                  <span>{opt.label}</span>
                                </Tooltip>
                              </MenuItem>
                            ))}
                          </Select>
                          <Chip
                            label={user.role.toUpperCase()}
                            size="small"
                            color={ROLE_CHIP_COLOR[user.role]}
                            variant={user.role === 'user' ? 'outlined' : 'filled'}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{formatDate(user.last_login_at)}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            size="small"
                            fullWidth
                            value={notesValue}
                            placeholder="Add a note…"
                            onChange={(e) => setPendingNotes((prev) => ({ ...prev, [user.uid]: e.target.value }))}
                            disabled={savingUid === user.uid}
                          />
                          {notesDirty && (
                            <Tooltip title="Save notes">
                              <IconButton color="primary" onClick={() => handleNotesSave(user)} sx={{ minWidth: 44, minHeight: 44 }}>
                                <SaveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={isSelf ? "You can't delete yourself" : 'Delete user record'}>
                          <span>
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(user)}
                              disabled={isSelf || savingUid === user.uid}
                              sx={{ minWidth: 44, minHeight: 44 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}
